import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import EmailField from '../fields/EmailField';
import NavRow from '../fields/NavRow';
import PhoneField from '../fields/PhoneField';
import { TaskDefinition, TaskStepRenderArgs } from '../QuestionnaireShell';
import { ContactValues } from '../types';
import { optionalPhoneString, phoneString, zodResolver } from '../validation';

const schema = z.object({
  email: z
    .string({ required_error: 'Email is required but is missing' })
    .min(1, 'Email is required but is missing')
    .email('Please enter a valid email address'),
  phone_OFFICE: phoneString('Office Phone Number is required but is missing').refine((v) => v.length > 0, {
    message: 'Office Phone Number is required but is missing',
  }),
  phone_MOBILE: optionalPhoneString(),
});

const DEFAULTS: ContactValues = { email: '', phone_OFFICE: '', phone_MOBILE: '' };

const ContactForm = ({ initialValues, onComplete, onBack, onCancelEdit, submitLabel }: TaskStepRenderArgs) => {
  const defaults = (initialValues as ContactValues | undefined) ?? DEFAULTS;
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<ContactValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit((values) => {
    const cleaned: ContactValues = {
      email: values.email,
      phone_OFFICE: values.phone_OFFICE,
      ...(values.phone_MOBILE ? { phone_MOBILE: values.phone_MOBILE } : {}),
    };
    onComplete(cleaned);
  });

  return (
    <form className="rnq-form" onSubmit={(e) => e.preventDefault()}>
      <EmailField control={control} name="email" label="Email Address" required />
      <PhoneField control={control} name="phone_OFFICE" label="Phone - Office" required />
      <PhoneField control={control} name="phone_MOBILE" label="Phone - Mobile" />
      <NavRow
        onBack={onBack}
        onCancelEdit={onCancelEdit}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={!isValid}
      />
    </form>
  );
};

const Contact: TaskDefinition = {
  key: 'task2',
  label: 'Contact',
  title: 'Contact Information',
  description: 'Please provide the best information to reach you.',
  render: (args) => <ContactForm {...args} />,
};

export default Contact;
